import { ServiceStatus } from "../../types";
import { productStatusToServiceStatus, serviceStatusToProductStatus } from "../product";
import { dollarsToSatoshis } from "../../utils";
import { productPricingRowFromService, productPricingRowToService } from "./shared";
/**
 * Converts service station group list item to UI format.
 * List items don't have billing info.
 * @param item - Service station group pricing list item
 * @returns UI-formatted station group pricing list item (no billingInfo)
 */
export const pricingStationGroupFromService = (item) => {
    return {
        id: item.id,
        name: item.name,
        metro: item.metro
    };
};
/**
 * Converts service station group detail to UI format.
 * Unflattens: service flat structure → UI nested billingInfo.
 * @param stationGroup - Service station group billing info (flat)
 * @returns UI-formatted station group with nested billingInfo
 */
export const pricingStationGroupBillingInfoFromService = (stationGroup) => {
    const { seconds: createdAtSeconds } = stationGroup.created_at || { seconds: 0 };
    const { seconds: updatedAtSeconds } = stationGroup.updated_at || { seconds: 0 };
    return {
        id: stationGroup.station_group_id,
        name: stationGroup.name,
        metro: stationGroup.metro,
        billingInfo: {
            status: serviceStatusToProductStatus(stationGroup.status),
            createdAt: createdAtSeconds ? new Date(createdAtSeconds * 1000) : undefined,
            updatedAt: updatedAtSeconds ? new Date(updatedAtSeconds * 1000) : undefined,
            createdBy: stationGroup.created_by,
            updatedBy: stationGroup.updated_by,
            products: stationGroup.products?.map(productPricingRowFromService)
        }
    };
};
/**
 * Converts UI station group to service format.
 * Flattens: UI nested billingInfo → service flat structure.
 * @param stationGroup - UI station group with nested billingInfo
 * @returns Service-formatted station group (flat)
 */
export const pricingStationGroupBillingInfoToService = (stationGroup) => {
    return {
        station_group_id: stationGroup.id,
        name: stationGroup.name,
        status: stationGroup.billingInfo
            ? productStatusToServiceStatus(stationGroup.billingInfo.status)
            : ServiceStatus.INVALID,
        metro: stationGroup.metro,
        created_at: stationGroup.billingInfo?.createdAt
            ? { seconds: Math.floor(stationGroup.billingInfo.createdAt.getTime() / 1000) }
            : undefined,
        updated_at: stationGroup.billingInfo?.updatedAt
            ? { seconds: Math.floor(stationGroup.billingInfo.updatedAt.getTime() / 1000) }
            : undefined,
        created_by: stationGroup.billingInfo?.createdBy,
        updated_by: stationGroup.billingInfo?.updatedBy,
        products: stationGroup.billingInfo?.products?.map(productPricingRowToService)
    };
};
/**
 * Converts station group to service upsert station group CPM request.
 * Extracts valid products (with id and cpm) and transforms CPMs from dollars to satoshis.
 * @param stationGroup - Full station group pricing list item
 * @returns Service-formatted upsert request
 */
export const toServiceUpsertStationGroupCpmRequestFromStationGroup = (stationGroup) => {
    const stationGroupId = stationGroup.id;
    const products = stationGroup.billingInfo?.products ?? [];
    const validProducts = products.filter((p) => !!p.id && p.cpm != null);
    return {
        upserts: validProducts.map((p) => ({
            prod_id: p.id,
            station_group_id: stationGroupId,
            cpm: dollarsToSatoshis(p.cpm)
        }))
    };
};
//# sourceMappingURL=stationGroups.js.map